import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, ScrollText, MapPin, Monitor, Globe } from "lucide-react";

const ACTION_COLORS = {
  Created: "text-slate-600 dark:text-slate-300",
  Updated: "text-indigo-600 dark:text-indigo-400",
  "Status changed to Approved": "text-amber-600 dark:text-amber-400",
  "Status changed to Paid": "text-emerald-600 dark:text-emerald-400",
  "Unlocked by Admin": "text-rose-600 dark:text-rose-400",
  "Reverted to Draft": "text-slate-600 dark:text-slate-300",
  Deleted: "text-rose-600 dark:text-rose-400",
};

export default function AuditLog() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    api.get("/audit", { params: { limit: 500 } }).then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
          <ScrollText className="w-5 h-5" /> Audit Log
        </h2>
        <p className="text-sm text-slate-500">Every action — who did what, when, and from where.</p>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        {logs === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : logs.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-slate-500">No audit entries yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">When</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Browser</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((a) => (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap">{formatDateTime(a.timestamp)}</TableCell>
                    <TableCell
                      className="font-mono text-sm font-medium cursor-pointer hover:underline whitespace-nowrap"
                      onClick={() => navigate(`/vouchers/${a.voucher_id}`)}
                      data-testid={`audit-voucher-${a.id}`}
                    >
                      {a.voucher_no}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] font-medium px-2 py-0.5" data-testid={`audit-module-${a.id}`}>
                        {a.module || "Voucher"}
                      </span>
                    </TableCell>
                    <TableCell className={`text-sm font-medium whitespace-nowrap ${ACTION_COLORS[a.action] || ""}`}>{a.action}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {a.user_name}
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-slate-400">{a.user_role}</span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap" data-testid={`audit-ip-${a.id}`}>{a.ip_address || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap" data-testid={`audit-location-${a.id}`}>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{a.location || "—"}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap" data-testid={`audit-device-${a.id}`}>
                      <span className="inline-flex items-center gap-1"><Monitor className="w-3 h-3 text-slate-400" />{a.device || "—"}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap" data-testid={`audit-browser-${a.id}`}>
                      <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3 text-slate-400" />{a.browser || "—"}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
